import * as React from 'react';
import { AccessToken, AccessTokenError, AccessTokenRequest, AccessTokenResponse } from '../Api/Authentication';
import { Failure as ApiFailure } from '../ApiClient';
import { useApi } from '../ApiHooks';
import { useErrorHandler } from '../ErrorHandler';
import { Initial, Loading, Result, Success } from '../Result';

export type AccessTokenResult = Result<AccessToken | null, ApiFailure<AccessTokenError>>;

export interface AccessTokenState {
	readonly accessToken: AccessTokenResult;
	readonly needsFetch: React.MutableRefObject<boolean>;
	readonly token: string | null;
	onChange(token: { expires: Date; id: string } | null): void;
}

type Value = [AccessTokenState, (state: AccessTokenState) => void];

export const AccessTokenContext = React.createContext<Value>([
	{
		accessToken: new Initial(),
		needsFetch: { current: false },
		onChange: () => {},
		token: null,
	},
	(_) => {},
]);
AccessTokenContext.displayName = 'AccessToken.Context';

export const useAccessToken = (): [
	AccessTokenResult,
	string | null,
	(accessToken: AccessToken | null) => void,
	() => Promise<void>
] => {
	const [state, setState] = React.useContext(AccessTokenContext);
	const [fetch] = useApi<AccessTokenRequest, AccessTokenResponse, AccessTokenError>();
	const { onError } = useErrorHandler();

	const update = (accessToken: AccessToken | null) => {
		const token = accessToken?.token ?? null;
		state.onChange(accessToken !== null ? { id: accessToken.token, expires: accessToken.expirationTime } : null);
		setState({ ...state, token, accessToken: new Success(accessToken) });
	};

	const load = async () => {
		if (state.token === null) {
			setState({ ...state, accessToken: new Success(null) });
			return;
		}

		setState({ ...state, accessToken: new Loading(state.accessToken.value) });
		const response = await fetch(new AccessTokenRequest());
		setState({ ...state, accessToken: response.map((value) => value.accessToken) });
		if (response.error?.hasUnhandled(Object.values(AccessTokenError)) ?? false) {
			onError(response.error);
		}
	};

	React.useEffect(() => {
		if (!state.needsFetch.current) {
			return undefined;
		}
		state.needsFetch.current = false;
		load();
		return undefined;
	}, []);

	return [state.accessToken, state.token, update, load];
};
